import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

interface PlatformUser {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isActive: boolean;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  authProvider: string | null;
  createdAt: string | null;
  lastLoginAt: string | null;
}

interface UsersResponse {
  items: PlatformUser[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const formatDate = (value: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const roleClasses: Record<string, string> = {
  admin: 'bg-red-50 text-red-700',
  photographer: 'bg-blue-50 text-blue-700',
  customer: 'bg-green-50 text-green-700',
};

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [role, setRole] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get<{ data: UsersResponse }>('/admin/users', {
        params: {
          page,
          limit: 50,
          ...(role ? { role } : {}),
          ...(search ? { search } : {}),
        },
      });
      setUsers(response.data.data.items);
      setTotal(response.data.data.total);
      setTotalPages(response.data.data.totalPages);
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [page, role, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const changeRole = (nextRole: string) => {
    setPage(1);
    setRole(nextRole);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Users</h1>
            <p className="mt-1 text-gray-500">Platform accounts pulled from the API</p>
          </div>
          <button
            onClick={loadUsers}
            className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
          >
            Refresh
          </button>
        </div>

        <div className="mb-6 rounded-lg bg-white p-4 shadow">
          <div className="flex flex-wrap gap-4">
            <select
              value={role}
              onChange={event => changeRole(event.target.value)}
              className="rounded-lg border border-gray-300 px-4 py-2"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="photographer">Photographer</option>
              <option value="customer">Customer</option>
            </select>
            <div className="flex min-w-72 flex-1 gap-2">
              <input
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                onKeyDown={event => event.key === 'Enter' && applySearch()}
                placeholder="Search by email, name, or user ID..."
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2"
              />
              <button
                onClick={applySearch}
                className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-lg bg-white shadow">
          {loading ? (
            <div className="p-12 text-center text-gray-500">Loading users...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Auth
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Last Login
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      users.map(user => (
                        <tr key={user.userId}>
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">
                              {[user.firstName, user.lastName].filter(Boolean).join(' ') || '-'}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                            <div className="text-xs text-gray-400">{user.userId}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`rounded-full px-2 py-1 text-xs font-semibold ${
                                roleClasses[user.role] || 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            <div>{user.isActive ? 'Active' : 'Inactive'}</div>
                            <div>{user.emailVerified ? 'Email verified' : 'Email pending'}</div>
                            <div>{user.twoFactorEnabled ? '2FA enabled' : '2FA off'}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {user.authProvider || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {formatDate(user.lastLoginAt)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between border-t bg-gray-50 px-6 py-3 text-sm text-gray-500">
                <span>Showing {users.length} of {total} users</span>
                <div className="flex items-center gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(current => Math.max(1, current - 1))}
                    className="rounded border px-3 py-1 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage(current => Math.min(totalPages, current + 1))}
                    className="rounded border px-3 py-1 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersPage;
